import { Upload } from 'lucide-react';
import * as React from 'react';
import { cn } from '@/lib/utils';

interface FileUploadDropAreaProps extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'> {
  disabled?: boolean;
  disabledText?: string;
  dropZoneText?: string;
  isDraggingOver?: boolean;
  onSelectFiles: () => void;
  onSelectFolder?: () => void;
  selectFilesButtonText?: string;
  selectFolderButtonText?: string;
  showSelectFolderButton?: boolean;
  supportedTypesText?: string;
  children?: React.ReactNode;
}

export const FileUploadDropArea = React.forwardRef<HTMLDivElement, FileUploadDropAreaProps>(({
  children,
  className,
  disabled = false,
  disabledText,
  dropZoneText = 'Drag files or folders here',
  isDraggingOver = false,
  onSelectFiles,
  onSelectFolder,
  selectFilesButtonText = 'Select Files',
  selectFolderButtonText = 'Select Folder',
  showSelectFolderButton = true,
  supportedTypesText,
  ...rest
}, ref) => (
  <div
    ref={ref}
    className={cn(
      'relative shrink-0 rounded-lg border-2 border-dashed p-4 text-center transition-all duration-150',
      disabled
        ? 'border-slate-200 bg-slate-50 opacity-60'
        : isDraggingOver
          ? 'border-crimson bg-crimson/5'
          : 'border-slate-200',
      className,
    )}
    {...rest}
  >
    <Upload className={cn(
      'mx-auto mb-1.5 h-6 w-6 max-sm:hidden [@media(max-height:720px)]:hidden',
      disabled ? 'text-slate-300' : isDraggingOver ? 'text-crimson' : 'text-slate-400',
    )} />
    <p className={cn(
      'mb-2 text-sm',
      disabled
        ? 'text-slate-400'
        : isDraggingOver
          ? 'font-medium text-crimson'
          : 'text-slate-600',
    )}>
      {disabled ? (disabledText || dropZoneText) : dropZoneText}
    </p>

    <div className="flex justify-center gap-2">
      <button
        type="button"
        onClick={onSelectFiles}
        disabled={disabled}
        className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
        title={disabled ? disabledText || '' : ''}
      >
        {selectFilesButtonText}
      </button>
      {showSelectFolderButton ? (
        <button
          type="button"
          onClick={onSelectFolder}
          disabled={disabled || !onSelectFolder}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
          title={disabled ? disabledText || '' : ''}
        >
          {selectFolderButtonText}
        </button>
      ) : null}
    </div>

    {supportedTypesText ? (
      <p className="mt-2 text-xs text-slate-500 max-sm:hidden [@media(max-height:720px)]:hidden">
        {supportedTypesText}
      </p>
    ) : null}
    {children}
  </div>
));

FileUploadDropArea.displayName = 'FileUploadDropArea';
